import type { RevenueBootstrapDTO } from "../models/revenueBootstrapDto";
import type { BootstrapSectionDTO } from "../models/clientOverviewBootstrapDto";
import type { BootstrapSectionEntity } from "../../domain/entities/ClientOverviewBootstrapEntity";
import type { RevenueBootstrapEntity } from "../../domain/entities/RevenueBootstrapEntity";
import type { RecentRevenueActivityEntity } from "../../domain/entities/RecentRevenueActivityEntity";
import type { RecentRevenueActivityResponseDTO } from "../api/clientOverviewApi";
import { lastMonthRevenueDtoToEntity } from "./lastMonthRevenueMapper";
import { revenueThisMonthDtoToEntity } from "./revenueThisMonthMapper";
import { keyMetricsRevenueDtoToEntity } from "./keyMetricsRevenueMapper";
import { twelveMonthRevenueTrendDtoToEntity } from "./twelveMonthRevenueTrendMapper";
import { revenueConcentrationDtoToEntity } from "./revenueConcentrationMapper";
import { receivablesAgingDtoToEntity } from "./receivablesAgingMapper";
import { recentRevenueActivityDtoToEntity } from "./recentRevenueActivityMapper";

function mapBootstrapSection<TDto, TEntity>(
  section: { status?: "ok" | "error"; data?: TDto | null; error?: string | null } | undefined,
  mapper: (dto: TDto) => TEntity,
): BootstrapSectionEntity<TEntity> {
  if (!section || section.status !== "ok" || section.data == null) {
    return {
      status: "error",
      data: null,
      error: section?.error ?? "Section unavailable",
    };
  }

  return {
    status: "ok",
    data: mapper(section.data),
    error: null,
  };
}

function mapRecentActivitySection(
  section:
    | BootstrapSectionDTO<RevenueBootstrapDTO["sections"]["recentActivity"]["data"]>
    | undefined,
): BootstrapSectionEntity<RecentRevenueActivityEntity> {
  if (!section || section.status !== "ok" || section.data == null) {
    return {
      status: "error",
      data: null,
      error: section?.error ?? "Section unavailable",
    };
  }

  return {
    status: "ok",
    data: recentRevenueActivityDtoToEntity(
      section.data as unknown as RecentRevenueActivityResponseDTO,
    ),
    error: null,
  };
}

export function revenueBootstrapDtoToEntity(dto: RevenueBootstrapDTO): RevenueBootstrapEntity {
  const sections = dto.sections ?? ({} as RevenueBootstrapDTO["sections"]);

  return {
    sections: {
      lastMonth: mapBootstrapSection(sections.lastMonth, lastMonthRevenueDtoToEntity),
      revenueThisMonth: mapBootstrapSection(sections.revenueThisMonth, revenueThisMonthDtoToEntity),
      keyMetrics: mapBootstrapSection(sections.keyMetrics, keyMetricsRevenueDtoToEntity),
      twelveMonthTrend: mapBootstrapSection(sections.twelveMonthTrend, twelveMonthRevenueTrendDtoToEntity),
      revenueConcentration: mapBootstrapSection(
        sections.revenueConcentration,
        revenueConcentrationDtoToEntity,
      ),
      receivablesAging: mapBootstrapSection(sections.receivablesAging, receivablesAgingDtoToEntity),
      recentActivity: mapRecentActivitySection(sections.recentActivity),
    },
    meta: {
      partial: dto.meta?.partial ?? false,
    },
  };
}
